import { useEffect, useRef, useState } from 'react';
import { settingsApi } from './api';
import SocialIcon, { SOCIAL_PLATFORMS } from '../../store/shared/components/SocialIcon';
import { mediaUrl, resolveSiteMedia } from '../../store/shared/utils/mediaUrl';
import { LOCAL_DEFAULTS } from '../../store/shared/constants/defaultAssets';
import { CONTACT_DEFAULTS } from '../../store/shared/constants/contactDefaults';
import { isSocialLinkActive, resolveSocialHref } from '../../store/shared/utils/socialLink';
import { formatPhoneDisplay } from '../../store/shared/utils/whatsapp';
import ValidatedInput, { validateFields } from '../../validations/ValidatedInput';
import './IntroScreen.css';

function FileDrop({ label, hint, accept, file, onChange, preview }) {
  const inputRef = useRef(null);
  const name = file?.name || null;

  return (
    <button
      type="button"
      className={`intro-drop${name || preview ? ' is-filled' : ''}`}
      onClick={() => inputRef.current?.click()}
    >
      <span className="intro-drop__label">{label}</span>
      {preview ? (
        <span className="intro-drop__preview">
          {accept?.includes('video') ? (
            <video src={preview} muted />
          ) : (
            <img src={preview} alt="" />
          )}
        </span>
      ) : (
        <span className="intro-drop__icon" aria-hidden>
          ↑
        </span>
      )}
      <span className="intro-drop__meta">
        {name ? name : hint || 'Click to choose a file'}
      </span>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        hidden
        onChange={(e) => onChange(e.target.files?.[0] || null)}
      />
    </button>
  );
}

const EMPTY_LINK = { platform: 'instagram', url: '', label: '', enabled: true };

export default function IntroScreen({ onMessage }) {
  const flash = (text) => onMessage?.(text);
  const [settings, setSettings] = useState(null);
  const [introMode, setIntroMode] = useState('logo');
  const [introVideoFile, setIntroVideoFile] = useState(null);
  const [introLogoFile, setIntroLogoFile] = useState(null);
  const [brandLogoFile, setBrandLogoFile] = useState(null);
  const [socialLinks, setSocialLinks] = useState([]);
  const [contactEmail, setContactEmail] = useState(CONTACT_DEFAULTS.email);
  const [contactHours, setContactHours] = useState(CONTACT_DEFAULTS.hours);
  const [contactAddress, setContactAddress] = useState(CONTACT_DEFAULTS.address);
  const [saving, setSaving] = useState(false);
  const [logoPreview, setLogoPreview] = useState('');
  const [brandPreview, setBrandPreview] = useState('');
  const [videoPreview, setVideoPreview] = useState('');

  const refresh = async () => {
    const st = await settingsApi.get().catch(() => ({ data: { data: null } }));
    const site = st.data.data;
    setSettings(site);
    if (site?.introMode) setIntroMode(site.introMode);
    setSocialLinks(
      site?.socialLinks?.length
        ? site.socialLinks.map((l) => ({ ...EMPTY_LINK, ...l }))
        : []
    );
    setContactEmail(site?.contactEmail || CONTACT_DEFAULTS.email);
    setContactHours(site?.contactHours || CONTACT_DEFAULTS.hours);
    setContactAddress(site?.contactAddress || CONTACT_DEFAULTS.address);
  };

  useEffect(() => {
    refresh().catch(() => flash('Could not load intro settings'));
  }, []);

  useEffect(() => {
    const urls = [];
    if (introLogoFile) {
      const u = URL.createObjectURL(introLogoFile);
      urls.push(u);
      setLogoPreview(u);
    } else {
      setLogoPreview(resolveSiteMedia(settings, 'introLogo') || LOCAL_DEFAULTS.introLogo);
    }
    if (brandLogoFile) {
      const u = URL.createObjectURL(brandLogoFile);
      urls.push(u);
      setBrandPreview(u);
    } else {
      setBrandPreview(resolveSiteMedia(settings, 'brandLogo') || LOCAL_DEFAULTS.brandLogo);
    }
    if (introVideoFile) {
      const u = URL.createObjectURL(introVideoFile);
      urls.push(u);
      setVideoPreview(u);
    } else {
      setVideoPreview(settings?.introVideo ? mediaUrl(settings.introVideo) : '');
    }
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [introLogoFile, brandLogoFile, introVideoFile, settings]);

  const updateLink = (idx, key, value) => {
    setSocialLinks((rows) => rows.map((row, i) => (i === idx ? { ...row, [key]: value } : row)));
  };

  const addLink = () => setSocialLinks((rows) => [...rows, { ...EMPTY_LINK }]);

  const removeLink = (idx) => setSocialLinks((rows) => rows.filter((_, i) => i !== idx));

  const saveIntroSettings = async (e) => {
    e.preventDefault();
    const errs = validateFields([
      { kind: 'email', value: contactEmail, required: true, label: 'Contact email' },
      { kind: 'text', value: contactHours, required: true, label: 'Business hours' },
    ]);
    if (errs.length) {
      flash(errs[0]);
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('introMode', introMode);
      fd.append('contactEmail', contactEmail.trim());
      fd.append('contactHours', contactHours.trim());
      fd.append('contactAddress', contactAddress.trim());
      fd.append(
        'socialLinks',
        JSON.stringify(socialLinks.filter((l) => l.platform === 'whatsapp' || l.url?.trim()))
      );
      if (introVideoFile) fd.append('introVideo', introVideoFile);
      if (introLogoFile) fd.append('introLogo', introLogoFile);
      if (brandLogoFile) fd.append('brandLogo', brandLogoFile);
      await settingsApi.update(fd);
      flash('Intro settings saved');
      setIntroVideoFile(null);
      setIntroLogoFile(null);
      setBrandLogoFile(null);
      await refresh();
    } catch (err) {
      flash(err.response?.data?.message || 'Settings update failed');
    } finally {
      setSaving(false);
    }
  };

  const activeLinks = socialLinks.filter((l) => isSocialLinkActive(l, settings?.whatsapp));

  return (
    <form className="intro-screen" onSubmit={saveIntroSettings}>
      <div className="intro-screen__main">
        <div className="intro-screen__panel">
          <div className="intro-screen__mode">
            <p className="intro-screen__label">Opening style</p>
            <div className="intro-screen__toggle" role="group" aria-label="Intro mode">
              <button
                type="button"
                className={introMode === 'logo' ? 'is-active' : ''}
                onClick={() => setIntroMode('logo')}
              >
                Logo animation
              </button>
              <button
                type="button"
                className={introMode === 'video' ? 'is-active' : ''}
                onClick={() => setIntroMode('video')}
              >
                Video
              </button>
            </div>
            <p className="intro-screen__note">
              {introMode === 'logo'
                ? 'Logo fades in, scales, then exits into the storefront.'
                : 'Video plays once, then fades into the storefront.'}
            </p>
          </div>

          <div className="intro-screen__uploads">
            <FileDrop
              label="Intro logo"
              hint="PNG / JPG — background is stripped"
              accept="image/*"
              file={introLogoFile}
              preview={logoPreview}
              onChange={setIntroLogoFile}
            />
            <FileDrop
              label="Brand logo"
              hint="Used in navbar & hero"
              accept="image/*"
              file={brandLogoFile}
              preview={brandPreview}
              onChange={setBrandLogoFile}
            />
            {introMode === 'video' && (
              <FileDrop
                label="Intro video"
                hint="MP4 or WebM, max ~60MB"
                accept="video/*"
                file={introVideoFile}
                preview={videoPreview}
                onChange={setIntroVideoFile}
              />
            )}
          </div>

          <div className="intro-contact">
            <p className="intro-screen__label">Contact Us details</p>
            <p className="intro-contact__note">
              Shown on the home page Contact section and cart stylist card. Phone uses{' '}
              <code>WHATSAPP_NUMBER</code> from server <code>.env</code>
              {settings?.whatsapp?.number ? ` · ${settings.whatsapp.number}` : ''}.
            </p>

            <div className="intro-contact__panel">
              <div className="intro-contact__fields">
                <label className="intro-contact__field">
                  <span>Email</span>
                  <ValidatedInput
                    kind="email"
                    className="intro-contact__input"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    placeholder="hello@nayavarnam.com"
                    required
                  />
                </label>
                <label className="intro-contact__field">
                  <span>Business hours</span>
                  <ValidatedInput
                    kind="text"
                    className="intro-contact__input"
                    value={contactHours}
                    onChange={(e) => setContactHours(e.target.value)}
                    placeholder="10am – 7pm IST"
                    required
                  />
                </label>
                <label className="intro-contact__field intro-contact__field--full">
                  <span>Address</span>
                  <ValidatedInput
                    kind="textarea"
                    className="intro-contact__input intro-contact__textarea"
                    value={contactAddress}
                    onChange={(e) => setContactAddress(e.target.value)}
                    rows={3}
                    placeholder="Studio address (optional)"
                  />
                </label>
              </div>

              <aside className="intro-contact__preview" aria-label="Contact preview">
                <span className="intro-contact__preview-label">Storefront preview</span>
                <ul className="intro-contact__preview-list">
                  {contactEmail && <li>{contactEmail}</li>}
                  {settings?.whatsapp?.number && (
                    <li>{formatPhoneDisplay(settings.whatsapp.number)}</li>
                  )}
                  {contactAddress && <li>{contactAddress}</li>}
                  {contactHours && <li>{contactHours}</li>}
                </ul>
                {!contactEmail && !contactHours && !contactAddress && (
                  <p className="intro-contact__preview-empty">Fill in the fields to preview.</p>
                )}
              </aside>
            </div>
          </div>

          <div className="intro-social">
            <div className="intro-social__head">
              <p className="intro-screen__label">Social links</p>
              <button type="button" className="intro-social__add" onClick={addLink}>
                + Add link
              </button>
            </div>
            <p className="intro-screen__note">
              Add any platform and URL. Shown as fixed icons on every storefront page.
            </p>
            {socialLinks.length === 0 && (
              <p className="intro-social__empty">No links yet — add Instagram, Facebook, YouTube, etc.</p>
            )}
            <div className="intro-social__list">
              {socialLinks.map((link, idx) => (
                <div key={`social-${idx}`} className="intro-social__row">
                  <label className="intro-social__platform">
                    <span>Platform</span>
                    <select
                      value={link.platform}
                      onChange={(e) => updateLink(idx, 'platform', e.target.value)}
                    >
                      {SOCIAL_PLATFORMS.map((p) => (
                        <option key={p.value} value={p.value}>{p.label}</option>
                      ))}
                    </select>
                  </label>
                  <label className="intro-social__url">
                    <span>{link.platform === 'whatsapp' ? 'WhatsApp' : 'Profile URL'}</span>
                    {link.platform === 'whatsapp' ? (
                      <p className="intro-social__wa-note">
                        Uses <code>WHATSAPP_NUMBER</code> and <code>WHATSAPP_DEFAULT_MESSAGE</code> from server <code>.env</code>
                        {settings?.whatsapp?.number ? ` · ${settings.whatsapp.number}` : ''}
                      </p>
                    ) : (
                      <input
                        type="url"
                        value={link.url}
                        onChange={(e) => updateLink(idx, 'url', e.target.value)}
                        placeholder="https://instagram.com/nayavarnam"
                      />
                    )}
                  </label>
                  {link.platform === 'custom' && (
                    <label className="intro-social__label-field">
                      <span>Label</span>
                      <input
                        type="text"
                        value={link.label}
                        onChange={(e) => updateLink(idx, 'label', e.target.value)}
                        placeholder="e.g. Pinterest"
                      />
                    </label>
                  )}
                  <label className="intro-social__enabled">
                    <input
                      type="checkbox"
                      checked={link.enabled !== false}
                      onChange={(e) => updateLink(idx, 'enabled', e.target.checked)}
                    />
                    <span>Show</span>
                  </label>
                  <button
                    type="button"
                    className="intro-social__remove"
                    onClick={() => removeLink(idx)}
                    aria-label="Remove link"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            {activeLinks.length > 0 && (
              <div className="intro-social__preview" aria-label="Social icon preview">
                {activeLinks.map((link, idx) => (
                  <a
                    key={`preview-${idx}`}
                    href={resolveSocialHref(link, settings?.whatsapp)}
                    className="intro-social__icon"
                    target="_blank"
                    rel="noreferrer"
                    title={link.label || link.platform}
                    onClick={(e) => e.preventDefault()}
                  >
                    <SocialIcon platform={link.platform} />
                  </a>
                ))}
              </div>
            )}
          </div>

          <div className="intro-screen__actions">
            <p className="intro-screen__path">
              Uploads → Supabase · URLs &amp; social links → MongoDB · Local <code>public/assets/</code> used as fallback only
            </p>
            <button type="submit" className="intro-screen__save" disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>

        <aside className="intro-screen__aside">
          <p className="intro-screen__label">Live preview</p>
          <div className="intro-preview">
            <div className="intro-preview__stage">
              {introMode === 'video' && videoPreview ? (
                <video src={videoPreview} controls muted playsInline />
              ) : logoPreview ? (
                <img src={logoPreview} alt="Intro logo preview" />
              ) : (
                <p className="intro-preview__empty">Upload a logo or video to preview</p>
              )}
            </div>
            <div className="intro-preview__meta">
              <div>
                <span>Mode</span>
                <strong>{introMode === 'logo' ? 'Logo animation' : 'Video'}</strong>
              </div>
              <div>
                <span>Brand mark</span>
                <strong>{brandPreview ? 'Ready' : 'Not set'}</strong>
              </div>
              <div>
                <span>Social links</span>
                <strong>{activeLinks.length}</strong>
              </div>
              <div>
                <span>Contact email</span>
                <strong>{contactEmail || '—'}</strong>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </form>
  );
}
